(function () {
  'use strict';

  /**
   * pp-wishlist.js — Cliente del endpoint /apps/crm/customer-wishlist.
   *
   * Responsabilidades:
   *   - 1 GET al boot para hidratar el state global de wishlist
   *   - Bind a todos los botones [data-pp-wishlist-btn] del DOM (active
   *     boot + observer para los que aparecen despues, e.g. cart drawer
   *     recommendations cargadas async)
   *   - Toggle add/remove via POST
   *   - Sync entre multiples botones del mismo producto via custom
   *     event 'pp-wishlist:change'
   *   - Manejo de: no_company (soft-fail, esconde botones), limit_reached
   *     (toast con copy), network errors
   *
   * Backend (handoff 2026-06-26):
   *   GET → { items: string[], has_company }
   *   POST { action:'add'|'remove', shopify_product_id }
   *     → 200 ok:true | 200 ok:false code:'no_company'
   *     | 409 code:'limit_reached' | 400/422
   *
   * Las paginas que necesiten el state mas alla del boton (e.g. la pagina
   * de favoritos) escuchan 'pp-wishlist:ready' y leen window.PPWishlist.
   */

  var ENDPOINT = '/apps/crm/customer-wishlist';
  var STORAGE_KEY = 'pp-wishlist-local';
  // Sin sesión no hay wishlist en el backend: el App Proxy responde 401 a
  // los anónimos, así que ni lo intentamos (el flag lo pone theme.liquid en
  // el propio script tag). Anónimo → directo al modo localStorage, que es
  // el mismo estado al que caía el 401.
  var LOGGED_IN = (function () {
    var el = document.querySelector('script[src*="pp-wishlist"][data-logged-in]');
    return !!(el && el.getAttribute('data-logged-in') === '1');
  })();
  // Logs de diagnóstico: apagados en producción para no ensuciar la
  // consola del cliente. Cambiar a true para depurar el flujo de wishlist.
  var DEBUG = false;
  var STATE = {
    items: null,          // null = no cargado, [] = cargado vacio, [...] = con items
    has_company: false,
    loaded: false,
    local_mode: false,    // true cuando estamos persistiendo a localStorage en lugar de backend
    pending: {}           // product_id → in-flight promise
  };

  // ── localStorage fallback ──────────────────────────────────────────
  // Cuando el backend reporta has_company:false (sea por bug del CRM o
  // porque el customer realmente es contact sin company), persistimos
  // los favoritos en localStorage. Transparente para el usuario; cuando
  // el backend se actualice para aceptar contact-level favoritos, los
  // items locales se podran migrar via merge en el siguiente GET.
  function loadLocal() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch (e) { return []; }
  }

  function saveLocal(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {}
  }

  // ── Toast helper (compartido con la pagina favoritos) ────────────────
  var toastEl = null;
  function showToast(text, type) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'pp-favoritos__toast';
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = text;
    toastEl.className = 'pp-favoritos__toast pp-favoritos__toast--visible' +
                        (type === 'error' ? ' pp-favoritos__toast--error' : '');
    clearTimeout(showToast._t);
    showToast._t = setTimeout(function () {
      toastEl.classList.remove('pp-favoritos__toast--visible');
    }, 2400);
  }

  function fireChange(productId, isActive) {
    document.dispatchEvent(new CustomEvent('pp-wishlist:change', {
      detail: { productId: String(productId), active: !!isActive }
    }));
  }

  function paintButton(btn, isActive) {
    btn.setAttribute('data-pp-wishlist-state', isActive ? 'active' : 'inactive');
    btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    btn.setAttribute('aria-label', isActive ? 'Quitar de favoritos' : 'Guardar en favoritos');
  }

  function isInWishlist(productId) {
    if (!STATE.loaded || !Array.isArray(STATE.items)) return false;
    return STATE.items.indexOf(String(productId)) !== -1;
  }

  function syncButton(btn) {
    // Antes ocultabamos el boton si !has_company. Ahora el feature esta
    // siempre disponible: backend para companies, localStorage como
    // fallback para contacts sin company (o cuando el backend tiene bug).
    var pid = btn.getAttribute('data-pp-wishlist-product-id');
    if (!pid) return;
    btn.hidden = false;
    paintButton(btn, isInWishlist(pid));
  }

  function syncAllButtons() {
    var btns = document.querySelectorAll('[data-pp-wishlist-btn]');
    for (var i = 0; i < btns.length; i++) syncButton(btns[i]);
  }

  function loadWishlist() {
    // Origen del JSON: la capa PPAccount (prefetch + dedup, arranca en el parse)
    // si existe; si no, el fetch directo de siempre — así otras páginas sin
    // PPAccount no rompen. PPAccount.get devuelve null en non-200 / parse fail /
    // red fallida; lo mapeamos a { items: [], has_company: false }, exactamente lo
    // que producía el path non-ok previo (que igual hace merge con localStorage).
    var source = !LOGGED_IN
      ? Promise.resolve({ items: [], has_company: false })
      : window.PPAccount
      ? window.PPAccount.get('customer-wishlist').then(function (data) {
          return data || { items: [], has_company: false };
        })
      : fetch(ENDPOINT, {
          method: 'GET',
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' }
        })
          .then(function (res) {
            // 401 (no logged in) o cualquier otro non-200 → tratamos como
            // "no aplica": no_company por default. Esconde los botones.
            if (!res.ok) {
              console.warn('[pp-wishlist] GET non-200:', res.status, res.statusText);
              return { items: [], has_company: false };
            }
            return res.json().catch(function (err) {
              console.warn('[pp-wishlist] GET JSON parse fail:', err);
              return { items: [], has_company: false };
            });
          });

    return source
      .then(function (data) {
        if (DEBUG) console.debug('[pp-wishlist] GET response:', data);
        var backendItems = Array.isArray(data.items) ? data.items.map(String) : [];
        STATE.has_company = !!data.has_company;

        if (STATE.has_company) {
          // Backend es source of truth para companies. Cache a localStorage
          // por si despues el customer pierde sesion / cambia de device.
          STATE.items = backendItems;
          STATE.local_mode = false;
          saveLocal(backendItems);
        } else {
          // Sin company: localStorage es source of truth. Hacer merge dedupe
          // con cualquier item que el backend devuelva (probablemente vacio,
          // pero por si acaso).
          var localItems = loadLocal();
          var merged = backendItems.slice();
          localItems.forEach(function (id) {
            if (merged.indexOf(id) === -1) merged.push(id);
          });
          STATE.items = merged;
          STATE.local_mode = true;
          saveLocal(merged);
        }
        STATE.loaded = true;
        syncAllButtons();
        document.dispatchEvent(new CustomEvent('pp-wishlist:ready', {
          detail: { items: STATE.items.slice(), has_company: STATE.has_company, local_mode: STATE.local_mode }
        }));
      })
      .catch(function (err) {
        console.warn('[pp-wishlist] GET network error:', err);
        // Sin red: usar localStorage como fallback final.
        STATE.items = loadLocal();
        STATE.has_company = false;
        STATE.local_mode = true;
        STATE.loaded = true;
        syncAllButtons();
        document.dispatchEvent(new CustomEvent('pp-wishlist:ready', {
          detail: { items: STATE.items.slice(), has_company: false, local_mode: true }
        }));
      });
  }

  function toggle(productId) {
    productId = String(productId);
    if (STATE.pending[productId]) return STATE.pending[productId];

    var currentlyIn = isInWishlist(productId);
    var action = currentlyIn ? 'remove' : 'add';

    // Optimistic update siempre. Si el backend acepta confirma, si rechaza
    // no_company cae al fallback de localStorage (transparente).
    if (currentlyIn) {
      STATE.items = STATE.items.filter(function (id) { return id !== productId; });
    } else {
      STATE.items.unshift(productId);
    }
    fireChange(productId, !currentlyIn);

    function persistLocalAndConfirm() {
      saveLocal(STATE.items);
      STATE.local_mode = true;
      return { ok: true, code: 'local' };
    }

    // Si ya estamos en local_mode (backend no tiene company para este
    // customer), persistir directo a localStorage sin tocar el endpoint.
    // Ahorra request y evita el toast de error.
    if (STATE.local_mode && !STATE.has_company) {
      var localResult = persistLocalAndConfirm();
      return Promise.resolve(localResult);
    }

    var promise = fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ action: action, shopify_product_id: productId })
    })
      .then(function (res) {
        return res.json().catch(function () { return {}; })
          .then(function (body) { return { status: res.status, body: body }; });
      })
      .then(function (resp) {
        var status = resp.status, body = resp.body || {};
        if (DEBUG) console.debug('[pp-wishlist] POST response:', { action: action, productId: productId, status: status, body: body });
        if (status === 200 && body.ok) {
          // Backend confirmo. Sincronizar localStorage como cache.
          saveLocal(STATE.items);
          return body;
        }

        if (status === 200 && body.code === 'no_company') {
          // Backend dice que el customer no tiene company. En lugar de
          // fallar, persistimos local — el favorito queda guardado para
          // esta sesion del browser. UX transparente: el corazon queda
          // marcado, sin toast de error.
          if (DEBUG) console.info('[pp-wishlist] backend reporto no_company; persistiendo en localStorage');
          STATE.has_company = false;
          return persistLocalAndConfirm();
        }

        // Otros errores: revertir el optimistic update.
        if (currentlyIn) {
          STATE.items.unshift(productId);
        } else {
          STATE.items = STATE.items.filter(function (id) { return id !== productId; });
        }
        fireChange(productId, currentlyIn);

        if (status === 409 && body.code === 'limit_reached') {
          showToast('Llegaste al limite de 500 favoritos', 'error');
        } else if (status === 401) {
          showToast('Tu sesion expiro. Recarga la pagina', 'error');
        } else {
          showToast('No pudimos guardar. Intentalo de nuevo', 'error');
          console.error('[pp-wishlist] POST fallo no esperado:', status, body);
        }
        return body;
      })
      .catch(function () {
        // Network error: en lugar de revertir, persistir en localStorage
        // para preservar la accion del usuario. Cuando vuelva la red, el
        // proximo POST podra reintentar.
        if (DEBUG) console.info('[pp-wishlist] POST network error; persistiendo en localStorage');
        return persistLocalAndConfirm();
      })
      .then(function (result) {
        delete STATE.pending[productId];
        return result;
      });

    STATE.pending[productId] = promise;
    return promise;
  }

  // ── Wire-up ─────────────────────────────────────────────────────────
  function bindButton(btn) {
    if (btn._ppWishlistBound) return;
    btn._ppWishlistBound = true;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var pid = btn.getAttribute('data-pp-wishlist-product-id');
      if (!pid) return;
      btn.disabled = true;
      // Pop animation antes del API call para que se sienta reactivo.
      btn.classList.add('pp-wishlist-btn--pop');
      setTimeout(function () { btn.classList.remove('pp-wishlist-btn--pop'); }, 360);
      toggle(pid).then(function () { btn.disabled = false; });
    });
  }

  function bindAllButtons() {
    var btns = document.querySelectorAll('[data-pp-wishlist-btn]');
    for (var i = 0; i < btns.length; i++) {
      bindButton(btns[i]);
      if (STATE.loaded) syncButton(btns[i]);
    }
  }

  // Observer para botones que aparecen async (e.g. cart drawer recs,
  // infinite scroll de colecciones, etc.)
  function startObserver() {
    if (!window.MutationObserver) return;
    var observer = new MutationObserver(function (mutations) {
      var hasNew = false;
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        for (var j = 0; j < m.addedNodes.length; j++) {
          var node = m.addedNodes[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-pp-wishlist-btn]')) {
            bindButton(node);
            if (STATE.loaded) syncButton(node);
            hasNew = true;
          } else if (node.querySelectorAll) {
            var nested = node.querySelectorAll('[data-pp-wishlist-btn]');
            for (var k = 0; k < nested.length; k++) {
              bindButton(nested[k]);
              if (STATE.loaded) syncButton(nested[k]);
              hasNew = true;
            }
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Listen cross-button sync: si otro boton del mismo producto cambia
  // el state, todos se actualizan.
  document.addEventListener('pp-wishlist:change', function (e) {
    var pid = e.detail.productId;
    var btns = document.querySelectorAll('[data-pp-wishlist-btn][data-pp-wishlist-product-id="' + pid + '"]');
    for (var i = 0; i < btns.length; i++) paintButton(btns[i], e.detail.active);
  });

  function boot() {
    bindAllButtons();
    startObserver();
    loadWishlist();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Expose para la pagina favoritos
  window.PPWishlist = {
    getItems: function () { return STATE.loaded ? STATE.items.slice() : null; },
    hasCompany: function () { return STATE.has_company; },
    isLocalMode: function () { return STATE.local_mode; },
    isLoaded: function () { return STATE.loaded; },
    toggle: toggle,
    reload: loadWishlist
  };
})();
